<template>
  <div class="autocomplete-select">
    <SkeleLoader v-if="isLoading" lines="1" />
    <div
      v-else
      :id="`autoCompleteSelect${autocompleteType}`"
      class="autocomplete-select__select-field"
      :class="{ isOpen: isOpen, disabled: isDisabled }"
      v-click-out-side="handleBlur"
    >
      <div
        class="autocomplete-select__selected-item"
        :class="{ hidden: isFocused }"
        :style="{ pTagComputedStyles }"
        @click="focusOnInputField"
      >
        <div v-if="selectedItem" class="icon">
          <span
            v-if="isFiatConfig"
            :class="`currency-flag currency-flag-${selectedItem.toLowerCase()}`"
          ></span>

          <CryptoIcon
            v-else
            :symbol="`${selectedItem.toLowerCase()}`"
            v-show="isCoinConfig"
          />
        </div>

        <div>{{ selectedItem }}</div>
      </div>
      <input
        ref="searchInput"
        :type="`${type}`"
        :id="`${name}-select`"
        :name="`${name}-select`"
        :placeholder="!selectedItem ? `${placeholder}` : ''"
        autocomplete="off"
        :aria-label="`${type}`"
        :aria-expanded="isOpen"
        v-model="search"
        @input="searchList"
        @focus="searchList"
        @keydown.enter="selectFirstItem"
        @keydown.escape="toggleSelect(false)"
        :tabindex="isDisabled ? -1 : 0"
      />
      <ul class="autocomplete-container" v-show="isOpen" ref="dropdownMenu">
        <li
          role="button"
          aria-labelledby="select-item"
          v-for="(item, index) in filteredList"
          :key="index"
          class="autocomplete-item"
          :class="{
            isActive: index === arrowCounter,
            disabled: item?.isDisabled,
          }"
          :tabindex="0"
          @click="setSearchValue(item)"
          @keydown.up="goToPreviousEl"
          @keydown.down="goToNextEl"
          @keydown.tab="goToNextEl"
          @keydown.enter="selectEl"
        >
          <template v-if="props.autocompleteType === 'country'">
            <div class="country-flag">
              <country-flag :country="item.code.toLowerCase()" size="big" />
            </div>
          </template>
          <p class="autocomplete-item__text">
            <slot name="selectItem" :item="item"></slot>

            <template v-if="!$slots.selectItem">
              {{ item }}
            </template>

            <pill v-if="item.isDisabled">Unsupported</pill>
          </p>
        </li>
      </ul>

      <div class="caret" 
        @click="onClickCaret"
      >
        <CaretDown v-if="!isDisabled || filteredList.length > 1" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { watch, onMounted, computed, ref, nextTick } from "vue";
import { watchOnce, whenever } from "@vueuse/core";
import CountryFlag from "vue-country-flag-next";

import useAutoCompleteSelect from "@/composables/ui/useAutoCompleteSelect.js";
import { vClickOutSide } from "@/composables/directives/useClickOutside";

import Pill from "@/components/ui/pills/Pill.vue";
import CaretDown from "@/components/icons/CaretDown.vue";
import SkeleLoader from "@/components/ui/loaders/SkeleLoader.vue";
import CryptoIcon from "@/components/icons/CryptoIcon.vue";

const props = defineProps({
  errors: { type: String, default: "" },
  items: { type: Array, required: true },
  name: { type: String, default: "default-select" },
  label: { type: String, default: "" },
  placeholder: {
    type: String,
    default: "",
  },
  type: { type: String, required: true },
  hasIcon: { type: Boolean, default: false },
  defaultValue: { type: String, default: "" },
  autocompleteType: { type: String, default: "country" },
  isLoading: { type: Boolean, default: false },
  isDisabled: { type: Boolean, default: false },
});
const emit = defineEmits(["update:autocomplete"]);
const isFiatConfig = props.name === "fiat";
const isCoinConfig = props.name === "coin";
const searchInput = ref(null);
const isFocused = ref(false);
const {
  isOpen,
  search,
  selectedItem,
  arrowCounter,
  searchList,
  filteredList,
  goToNextEl,
  selectEl,
  goToPreviousEl,
  setSearchValue,
  selectFirstItem,
  toggleSelect,
  updateCurrentList,
  handleBlur,
} = useAutoCompleteSelect(props.items, props.autocompleteType);

whenever(selectedItem, () => {
  emit("updateAutocomplete", selectedItem.value);
});

watch(
  () => props.defaultValue,
  (value) => {
    if (value) selectedItem.value = value;
  },
  { deep: true }
);

watch(
  () => props.items,
  (newItems, prevItems) => {
    updateCurrentList(newItems);
  },
  { deep: true }
);

onMounted(() => {
  selectedItem.value = props.autocomplete;
});

const focusOnInputField = () => {
  nextTick(() => {
    searchInput.value.focus();
  });
  searchList();
};

const onClickCaret = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    focusOnInputField()
  } else {
    search.value = '';
  }
}

watch(
  () => selectedItem,
  () => {
    if (searchInput.value) searchInput.value.blur();
  },
  { deep: true }
);
</script>
<style lang="scss" scoped>
$dropdown-max-height: 200px;
$flag-dimension: 32px;
.autocomplete-select {
  &__select-field {
    min-width: 115px;
    display: flex;
    align-items: center;

    svg {
      &.caret-down {
        margin-top: 5px;
        margin-right: 0;
        right: 15px;
        left: unset;
        transform: rotate(0deg) translate(-50%, -50%);
        transition: transform 250ms;
      }
    }

    .autocomplete-container {
      max-height: $dropdown-max-height;
      width: 100%;
      z-index: 1;
      position: absolute;
      transform: translateX(-50%);
      top: 100%;
      left: 50%;
      overflow-y: scroll;
      border: var(--dropdown-border);
      background-color: var(--dropdown-background);
      border-radius: $border-radius-dropdown;
      box-shadow: 3px 2px 2px -2px rgba(0, 0, 0, 0.65);

      .autocomplete-item {
        margin: 0 auto;
        padding-block: 1.5rem;
        display: grid;
        grid-template-columns: 1fr 5fr;
        align-items: center;
        padding-left: 2rem;
        border-bottom: var(--dropdown-border);
        @include breakpoint($min-m) {
          padding-left: 4rem;
        }

        &:hover,
        &:focus-visible,
        &.isActive {
          cursor: pointer;
          width: 100%;
          background-color: var(--input-background);
          color: white;
        }

        &.disabled {
          padding-right: 4rem;

          .autocomplete-item__text {
            // account for disabled pill
            justify-content: space-between;
            width: 100%;
          }
        }

        &:last-child {
          border: none;
        }

        .country-flag {
          height: $flag-dimension;
          width: $flag-dimension;
          margin-right: 1.5rem;
          position: relative;
          display: block;
          border-radius: 50%;
          overflow: hidden;

          .flag {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
          }
        }

        &__text {
          display: flex;
          font-size: 2rem;
          align-items: center;

          .country-iso2 {
            flex: 0 1 auto;
          }
        }
      }
    }

    &.isOpen {
      svg.caret-down {
        transform: rotate(180deg) translate(50%, 50%);
        margin-top: 5px;
        margin-left: 1.25rem;
      }

      input {
        min-width: 69px; // autocomplete-select__selected-item's icon + selected item min width
        padding: 1rem;
        background-color: var(--input-background);
        border-radius: 1.5rem;
        border: 1px solid rgba(0, 114, 206, 0);
        transition: border 250ms;
        font-size: $font-input-size;

        &:focus {
          border: 1px solid #0072ce;
        }

        @include breakpoint($min-m) {
          font-size: 2rem;
        }
      }
    }

    input {
      background-color: transparent;
      width: 0;
      font-size: 1.6rem;
    }
  }

  &__selected-item {
    display: flex;
    //position: absolute;
    //top: 50%;
    //transform: translateY(-50%);
    font-size: 2rem;

    .icon {
      display: flex;
      margin-right: 0.5rem;
      .currency-flag {
        width: 25px;
        height: 25px;
        border-radius: 50%;
      }
    }

    .isOpen & {
      display: none;
    }

    //position: absolute;
    //left: 50%;
    //top: 50%;
    //transform: translate(-50%, -50%);
  }
}
</style>
